import { useCallback, useEffect, useRef } from 'react';

/**
 * Trailing-edge debounce hook.
 *
 * Returns `{ run, cancel }`:
 *   - `run(...args)` schedules `fn(...args)` after `delay` ms, replacing any
 *     pending call. Identity is stable across renders.
 *   - `cancel()` clears any pending timer.
 *
 * The latest `fn` is captured via a ref so callers can pass an inline arrow
 * without thrashing the debounced function's identity. The pending timer is
 * cleared automatically on unmount.
 *
 * Note: this is a trailing-edge debounce only (no leading edge, no flush).
 * Hooks with subtly different semantics (e.g. `useSupabaseSync.js`, where
 * the debounce is keyed on a re-running effect's dep array, or any
 * leading-edge / flush-on-unmount variant) should keep their local
 * implementation.
 *
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @param {number} delay
 * @returns {{ run: (...args: Parameters<F>) => void, cancel: () => void }}
 */
export function useDebounce(fn, delay) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);

  useEffect(() => { fnRef.current = fn; }, [fn]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const run = useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      fnRef.current(...args);
    }, delay);
  }, [delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { run, cancel };
}
